import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { PostCard } from "../components/PostCard";
import { StoryBar, StoryViewer } from "../components/Stories";
import { CreateModal } from "../components/CreateModal";
import { Loading, Empty, Skeleton } from "../components/common";
import { Newspaper, RefreshCw } from "lucide-react";

export default function Feed() {
  const [posts, setPosts] = useState([]);
  const [stories, setStories] = useState([]);
  const [lives, setLives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [storyOpen, setStoryOpen] = useState(-1);
  const [createStory, setCreateStory] = useState(false);
  const [scope, setScope] = useState("all");
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const pullStart = useRef(null);
  const swipeStart = useRef(null);
  const sentinel = useRef();

  const load = useCallback(async (reset = false) => {
    const s = reset ? 0 : skip;
    const { data } = await api.get(`/feed?skip=${s}&limit=10&scope=${scope}`);
    setPosts((p) => (reset ? data : [...p, ...data]));
    setHasMore(data.length === 10);
    setSkip(s + data.length);
    setLoading(false);
  }, [skip, scope]);

  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    try {
      await load(true);
      api.get("/stories").then(({ data }) => setStories(data)).catch(() => {});
      api.get("/live").then(({ data }) => setLives(data)).catch(() => {});
    } finally { setTimeout(() => setRefreshing(false), 400); }
  }, [load]); // eslint-disable-line

  const onTouchStart = (e) => {
    const t = e.touches[0];
    pullStart.current = window.scrollY <= 0 ? t.clientY : null;
    // Never switch tabs from swipes that start on post media (image carousels, videos) or any scrollable row.
    const blocked = e.target.closest?.("img, video, [data-no-tab-swipe], [data-testid^='post-media-'], [data-testid^='post-carousel-'], [data-testid^='post-video-'], .overflow-x-auto, .snap-x");
    swipeStart.current = blocked ? null : { x: t.clientX, y: t.clientY, at: Date.now() };
  };
  const onTouchMove = (e) => {
    if (pullStart.current == null || refreshing) return;
    const d = e.touches[0].clientY - pullStart.current;
    if (d > 0 && window.scrollY <= 0) setPull(Math.min(d * 0.5, 80));
  };
  const onTouchEnd = (e) => {
    if (pull > 55 && !refreshing) refreshAll();
    setPull(0); pullStart.current = null;
    // horizontal swipe to switch For You / Following
    if (swipeStart.current) {
      const t = e.changedTouches[0];
      const dx = t.clientX - swipeStart.current.x;
      const dy = t.clientY - swipeStart.current.y;
      if (Math.abs(dx) > 120 && Math.abs(dx) > Math.abs(dy) * 2.5 && Date.now() - swipeStart.current.at < 600) {
        if (dx < 0) setScope("following");
        else setScope("all");
      }
      swipeStart.current = null;
    }
  };

  useEffect(() => { api.get("/stories").then(({ data }) => setStories(data)).catch(() => {}); api.get("/live").then(({ data }) => setLives(data)).catch(() => {}); load(true); // eslint-disable-next-line
  }, []);

  useEffect(() => { setLoading(true); setSkip(0); load(true); // eslint-disable-next-line
  }, [scope]);

  useEffect(() => {
    const refresh = () => { load(true); api.get("/stories").then(({ data }) => setStories(data)); api.get("/live").then(({ data }) => setLives(data)).catch(() => {}); };
    window.addEventListener("vs:refresh", refresh);
    return () => window.removeEventListener("vs:refresh", refresh);
  }, [load]);

  useEffect(() => {
    const el = sentinel.current; if (!el) return;
    const ob = new IntersectionObserver((e) => { if (e[0].isIntersecting && hasMore && !loading) load(); }, { rootMargin: "400px" });
    ob.observe(el); return () => ob.disconnect();
  }, [hasMore, loading, load]);

  return (
    <div className="px-4 md:px-6 py-5 max-w-[640px] mx-auto" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} data-testid="feed-page">
      {(pull > 0 || refreshing) && (
        <div className="flex justify-center overflow-hidden transition-all" style={{ height: refreshing ? 40 : pull }} data-testid="feed-pull-refresh">
          <RefreshCw size={22} className={`text-eblue mt-2 ${refreshing ? "animate-spin" : ""}`} style={{ transform: refreshing ? undefined : `rotate(${pull * 4}deg)` }} />
        </div>
      )}
      <div className="mb-4 surface rounded-2xl p-3"><StoryBar groups={stories} lives={lives} onAdd={() => setCreateStory(true)} onOpen={setStoryOpen} /></div>
      <div className="flex items-center gap-1 mb-4 surface rounded-full p-1 w-fit mx-auto" data-testid="feed-scope-tabs">
        {[["all", "For You"], ["following", "Following"]].map(([k, label]) => (
          <button key={k} onClick={() => setScope(k)} data-testid={`feed-scope-${k}`}
            className={`px-5 h-9 rounded-full text-sm font-semibold transition-colors ${scope === k ? "btn-blue text-white" : "text-muted-foreground hover:text-white"}`}>{label}</button>
        ))}
      </div>
      {loading ? (
        <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="surface rounded-2xl p-4"><div className="flex gap-3 mb-3"><Skeleton className="w-11 h-11 rounded-full" /><div className="flex-1 space-y-2"><Skeleton className="h-3 w-32" /><Skeleton className="h-2 w-20" /></div></div><Skeleton className="h-48 w-full" /></div>)}</div>
      ) : posts.length === 0 ? (
        scope === "following"
          ? <Empty icon={Newspaper} title="No posts from people you follow yet" hint="Follow more creators, or switch to For You to discover the industry." />
          : <Empty icon={Newspaper} title="Your feed is quiet" hint="Follow creators and post something to bring your feed to life." />
      ) : (
        <div className="space-y-4">{posts.map((p) => <PostCard key={p.id} post={p} />)}</div>
      )}
      <div ref={sentinel} className="h-10" />
      {hasMore && !loading && posts.length > 0 && <div className="py-4"><Loading label="Loading more…" /></div>}

      {storyOpen >= 0 && <StoryViewer groups={stories} startIndex={storyOpen} onClose={() => setStoryOpen(-1)} />}
      <CreateModal open={createStory} onClose={() => setCreateStory(false)} defaultTab="story" onCreated={() => api.get("/stories").then(({ data }) => setStories(data))} />
    </div>
  );
}
